import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import './admin-dashboard.scss';
import { FaEdit, FaTrash, FaUserPlus, FaUserMinus, FaEnvelope, FaFilter, FaBlog } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { subscriptionService, PremiumUser } from '../../services/subscription';
import { toast } from '../../utils/toast';

interface User {
  id: string;
  email: string;
  created_at: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface MessageModalProps {
  user: User;
  onClose: () => void;
  onSend: (userId: string, message: string) => Promise<void>;
}

const formatDate = (date: string | Date) => {
  const d = new Date(date);
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month} ${day}, ${year}`;
};

const MessageModal: React.FC<MessageModalProps> = ({ user, onClose, onSend }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    try {
      setSending(true);
      await onSend(user.id, message);
      onClose();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="message-modal-overlay">
      <div className="message-modal">
        <div className="message-modal-header">
          <h3>Message to {user.first_name} {user.last_name}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message here..."
            rows={5}
          />
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={sending}>Cancel</button>
            <button type="submit" disabled={sending || !message.trim()}>
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [premiumUsers, setPremiumUsers] = useState<Record<string, PremiumUser>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<'all' | 'premium' | 'trial' | 'expired'>('all');
  const [messageModalUser, setMessageModalUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const ADMIN_ID = process.env.REACT_APP_ADMIN_USER_ID;

  // Check if current user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser?.user_metadata?.role || currentUser.user_metadata.role !== 'admin') {
        navigate('/');
      }
    };
    checkAdmin();
  }, [navigate]);

  // Fetch users and premium status
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all profiles from Supabase
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('*');

        if (profilesError) throw profilesError;
        if (!profiles) throw new Error('No profiles returned from Supabase');

        // Get premium users (must await)
        const premiumUsersList = await subscriptionService.getAllPremiumUsers();

        // Build a map of email -> PremiumUser
        const premiumUsersMap = premiumUsersList.reduce<Record<string, PremiumUser>>(
          (acc, pUser) => {
            acc[pUser.email] = pUser;
            return acc;
          },
          {}
        );

        // Transform Supabase profiles into our local User[] structure
        const transformedUsers = profiles.map((profile: any) => ({
          id: profile.id,
          email: profile.email || '',
          created_at: profile.created_at,
          first_name: profile.first_name || 'N/A',
          last_name: profile.last_name || 'N/A',
          role: profile.role || 'user',
        }));

        setUsers(transformedUsers);
        setPremiumUsers(premiumUsersMap);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleUpdateUser = async (userId: string, updates: any) => {
    try {
      // Update profile in Supabase
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          first_name: updates.firstName,
          last_name: updates.lastName,
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Update user metadata (role)
      const { error: metadataError } = await supabase.rpc('update_user_metadata', {
        user_id: userId,
        metadata: { role: updates.role },
      });

      if (metadataError) throw metadataError;

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                first_name: updates.firstName || '',
                last_name: updates.lastName || '',
                role: updates.role || 'user',
              }
            : u
        )
      );

      setEditingUser(null);
      toast.success('User updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const { error } = await supabase.rpc('delete_user', {
        user_id: userId,
      });

      if (error) throw error;

      setUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateSubscription = async (email: string, status: 'trial' | 'premium') => {
    try {
      const updatedUser = await subscriptionService.updateSubscription(email, status);
      setPremiumUsers((prev) => ({
        ...prev,
        [email]: updatedUser,
      }));
      toast.success(
        `User ${status === 'premium' ? 'upgraded to premium' : 'downgraded to trial'}`
      );
    } catch (err) {
      console.error('Error updating subscription:', err);
      toast.error(`Failed to update subscription: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleCancelSubscription = async (email: string) => {
    try {
      const success = await subscriptionService.cancelSubscription(email);
      if (success) {
        setPremiumUsers((prev) => {
          // Create new object with the updated user status
          const newPremiumUsers = { ...prev };
          if (newPremiumUsers[email]) {
            newPremiumUsers[email] = {
              ...newPremiumUsers[email],
              status: 'expired'
            };
          }
          return newPremiumUsers;
        });
        toast.success('Subscription cancelled successfully');
      } else {
        toast.error('Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      toast.error('Failed to cancel subscription');
    }
  };

  const handleSendMessage = async (userId: string, messageContent: string) => {
    if (!user || !ADMIN_ID) return;
    
    try {
      const userToMessage = users.find(u => u.id === userId);
      if (!userToMessage) throw new Error("User not found");

      const message = {
        sender_id: ADMIN_ID,
        receiver_id: userId,
        content: messageContent,
        read: false,
        created_at: new Date().toISOString(),
        is_admin_message: true
      };

      const { error } = await supabase
        .from('messages')
        .insert([message]);

      if (error) throw error;
      
      toast.success(`Message sent to ${userToMessage.first_name} ${userToMessage.last_name}`);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
      throw err;
    }
  };

  // Filter users based on search term and subscription filter
  const filteredUsers = users.filter(u => {
    // First, filter by search term
    const matchesSearch = 
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Then, filter by subscription type if needed
    if (subscriptionFilter === 'all') {
      return matchesSearch;
    } else {
      const userStatus = premiumUsers[u.email]?.status || 'expired';
      return matchesSearch && userStatus === subscriptionFilter;
    }
  });

  // Calculate pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  if (loading) return <div className="admin-dashboard loading">Loading...</div>;
  if (error) return <div className="admin-dashboard error">Error: {error}</div>;

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>

      <div className="dashboard-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="subscription-filter">
          <span><FaFilter /> Filter by:</span>
          <button 
            className={subscriptionFilter === 'all' ? 'active' : ''} 
            onClick={() => setSubscriptionFilter('all')}
          >
            All
          </button>
          <button 
            className={subscriptionFilter === 'premium' ? 'active' : ''} 
            onClick={() => setSubscriptionFilter('premium')}
          >
            Premium
          </button>
          <button 
            className={subscriptionFilter === 'trial' ? 'active' : ''} 
            onClick={() => setSubscriptionFilter('trial')}
          >
            Trial
          </button>
          <button 
            className={subscriptionFilter === 'expired' ? 'active' : ''} 
            onClick={() => setSubscriptionFilter('expired')}
          >
            Expired
          </button>
        </div>
      </div>

      <div className="admin-modules">
        <div className="admin-module blog-module">
          <h2><FaBlog /> Blog Management</h2>
          <p>Write, edit, and manage blog posts and categories</p>
          <Link to="/admin/blog" className="module-button">
            Manage Blog
          </Link>
          <Link to="/blog" className="secondary-button" target="_blank">
            View Blog
          </Link>
        </div>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Subscription</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>
                  {editingUser?.id === user.id ? (
                    <div className="edit-fields">
                      <input
                        type="text"
                        defaultValue={user.first_name}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            first_name: e.target.value,
                          })
                        }
                      />
                      <input
                        type="text"
                        defaultValue={user.last_name}
                        onChange={(e) =>
                          setEditingUser({
                            ...editingUser,
                            last_name: e.target.value,
                          })
                        }
                      />
                    </div>
                  ) : (
                    `${user.first_name} ${user.last_name}`
                  )}
                </td>
                <td>
                  {editingUser?.id === user.id ? (
                    <select
                      defaultValue={user.role}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          role: e.target.value,
                        })
                      }
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    user.role
                  )}
                </td>
                <td>
                  {premiumUsers[user.email]?.status || 'No subscription'}
                  {premiumUsers[user.email] && (
                    <div className="subscription-dates">
                      <small>
                        Start: {formatDate(premiumUsers[user.email].startDate)}
                        <br />
                        End: {formatDate(premiumUsers[user.email].endDate)}
                      </small>
                    </div>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    {editingUser?.id === user.id ? (
                      <>
                        <button
                          onClick={() =>
                            handleUpdateUser(user.id, {
                              firstName: editingUser.first_name,
                              lastName: editingUser.last_name,
                              role: editingUser.role,
                            })
                          }
                          className="save"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="cancel"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditingUser(user)} title="Edit user">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDeleteUser(user.id)} title="Delete user">
                          <FaTrash />
                        </button>
                        <button
                          onClick={() => handleUpdateSubscription(user.email, 'premium')}
                          title="Upgrade to Premium"
                          className={premiumUsers[user.email]?.status === 'premium' ? 'active' : ''}
                        >
                          <FaUserPlus />
                        </button>
                        <button
                          onClick={() => handleUpdateSubscription(user.email, 'trial')}
                          title="Downgrade to Trial"
                          className={premiumUsers[user.email]?.status === 'trial' ? 'active' : ''}
                        >
                          <FaUserMinus />
                        </button>
                        <button
                          onClick={() => premiumUsers[user.email] ? handleCancelSubscription(user.email) : null}
                          title="Cancel Subscription"
                          className={premiumUsers[user.email]?.status === 'expired' ? 'active' : ''}
                          disabled={!premiumUsers[user.email] || premiumUsers[user.email]?.status === 'expired'}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => setMessageModalUser(user)}
                          title="Message User"
                          className="message-btn"
                        >
                          <FaEnvelope />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage - 1)} 
            disabled={currentPage === 1}
            className="page-btn"
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                className={currentPage === i + 1 ? 'active' : ''}
              >
                {i + 1}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => paginate(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages} ({filteredUsers.length} users)
          </span>
        </div>
      )}

      {messageModalUser && (
        <MessageModal 
          user={messageModalUser} 
          onClose={() => setMessageModalUser(null)}
          onSend={handleSendMessage}
        />
      )}
    </div>
  );
}
